import { useId, useState } from 'react';
import { InputNumber } from 'antd';

import { Button, Dialog, Field, Input, Select, Textarea } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { loiTheoTruong } from '../../../../lib/errors';
import { useToast } from '../../../../lib/toast';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { MUC_DO_LOI } from '../../constants';
import { useSuaCode, useTaoCode } from '../../hooks/useCauHinhLoi';
import {
  GIAN_CACH_TOI_DA,
  GIOI_HAN,
  MUC_DO,
  THU_LAI,
  chuanHoaCode,
  codeThanhForm,
  codeTrong,
  kiemCode,
} from '../../nghiep-vu/loi';
import type { LoiCode } from '../../nghiep-vu/loi';
import type { CodeLoi, KhiHetLan, MucCho, ThanCodeLoi } from '../../types';
import { DongBatTat } from '../chung';

const KHI_HET: KhiHetLan[] = ['SKIP', 'STOP'];

const tuO = (v: number | null): number => (v === null ? Number.NaN : v);
const raO = (v: number): number | null => (Number.isNaN(v) ? null : v);

/**
 * Tạo hoặc sửa một code lỗi (Bảng 1). Tạo từ exception chờ thì exception đó thành dòng chi tiết đầu tiên của code mới.
 * `onDaLuu` nhận mã code vừa lưu để màn chọn nó. Lỗi của máy chủ hiện trong hộp, hộp không đóng.
 */
export function HopCode({
  goc,
  cho,
  onClose,
  onDaLuu,
}: {
  goc: CodeLoi | null;
  cho: MucCho | null;
  onClose: () => void;
  onDaLuu: (code: string) => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const formId = useId();
  const tao = useTaoCode();
  const sua = useSuaCode();
  const dang = goc ? sua : tao;
  const [than, setThan] = useState<ThanCodeLoi>(() => (goc ? codeThanhForm(goc) : codeTrong()));
  const [loi, setLoi] = useState<LoiCode>({});

  const dat = <K extends keyof ThanCodeLoi>(k: K, v: ThanCodeLoi[K]) => {
    setThan((cu) => ({ ...cu, [k]: v }));
    dang.reset();
  };

  const truong = loiTheoTruong(dang.error);
  const loiChung = dang.error && Object.keys(truong).length === 0 ? dichLoi(dang.error) : null;

  const gui = () => {
    const thay = kiemCode(than);
    setLoi(thay);
    if (Object.keys(thay).length > 0 || dang.isPending) return;
    const du = chuanHoaCode(than);
    const xong = (c: { code: string }) => {
      toast.success(t(goc ? 'rpaLoi.form.daLuu' : 'rpaLoi.form.daTao', { ma: c.code }));
      onDaLuu(c.code);
      onClose();
    };
    if (goc) sua.mutate({ code: goc.code, than: du }, { onSuccess: xong });
    else tao.mutate(cho ? { ...du, pendingId: cho.id } : du, { onSuccess: xong });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      width={720}
      destroyOnHidden
      title={goc ? t('rpaLoi.form.suaTieuDe', { ma: goc.code }) : t('rpaLoi.form.taoTieuDe')}
      footer={
        <div className="space-y-3">
          {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dang.isPending}>
              {dang.isPending ? t('chung.dangLuu') : t(goc ? 'rpaLoi.form.luu' : 'rpaLoi.form.tao')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-5 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        {cho && (
          <div className="rounded-lg border border-default bg-subtle px-3 py-2.5 text-xs">
            <p className="text-content-muted">{t('rpaLoi.form.tuCho')}</p>
            <p className="mt-1 break-all font-mono text-sm text-content-primary">{cho.exception}</p>
            {cho.sampleMessage && <p className="mt-1 break-words text-content-secondary">{cho.sampleMessage}</p>}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label={t('rpaLoi.form.ten')}
            required
            hint={t('rpaLoi.form.tenGoiY')}
            error={loi.name ? t('rpaLoi.form.loiTen') : truong['/name']}
            className="sm:col-span-2"
          >
            {(p) => (
              <Input
                {...p}
                autoFocus
                maxLength={GIOI_HAN.ten}
                value={than.name}
                placeholder={t('rpaLoi.form.tenMau')}
                onChange={(e) => dat('name', e.target.value)}
              />
            )}
          </Field>
          <Field label={t('rpaLoi.form.tinhChat')} hint={t('rpaLoi.form.tinhChatGoiY')}>
            {(p) => (
              <Select<'co' | 'khong'>
                {...p}
                className="w-full"
                value={than.foreseen ? 'co' : 'khong'}
                onChange={(v) => dat('foreseen', v === 'co')}
                options={[
                  { value: 'co', label: t('rpaLoi.form.luongTruoc') },
                  { value: 'khong', label: t('rpaLoi.form.batNgo') },
                ]}
              />
            )}
          </Field>
          <Field label={t('rpaLoi.form.mucDo')}>
            {(p) => (
              <Select
                {...p}
                className="w-full"
                value={than.severity}
                onChange={(v) => dat('severity', v)}
                options={MUC_DO.map((m) => ({ value: m, label: t(MUC_DO_LOI[m].khoa) }))}
              />
            )}
          </Field>
          <Field label={t('rpaLoi.form.moTa')} hint={t('rpaLoi.form.moTaGoiY')} className="sm:col-span-2" error={truong['/description']}>
            {(p) => (
              <Textarea {...p} rows={2} maxLength={GIOI_HAN.moTa} value={than.description} onChange={(e) => dat('description', e.target.value)} />
            )}
          </Field>
        </div>

        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-content-primary">{t('rpaLoi.form.cachXuLy')}</h3>
            <p className="text-xs text-content-muted">{t('rpaLoi.form.cachXuLyGoiY')}</p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field
              label={t('rpaLoi.form.soLan')}
              hint={t('rpaLoi.form.soLanGoiY', { max: THU_LAI.max })}
              error={loi.maxRetries ? t('rpaLoi.form.loiSoLan') : truong['/maxRetries']}
            >
              {(p) => (
                <InputNumber
                  {...p}
                  className="w-full"
                  status={p['aria-invalid'] ? 'error' : undefined}
                  min={THU_LAI.min}
                  max={THU_LAI.max}
                  precision={0}
                  value={raO(than.maxRetries)}
                  onChange={(v) => dat('maxRetries', tuO(v))}
                />
              )}
            </Field>
            <Field
              label={t('rpaLoi.form.gianCach')}
              hint={t('rpaLoi.form.gianCachGoiY', { max: GIAN_CACH_TOI_DA })}
              error={loi.retryDelaySeconds ? t('rpaLoi.form.loiGianCach') : truong['/retryDelaySeconds']}
            >
              {(p) => (
                <InputNumber
                  {...p}
                  className="w-full"
                  status={p['aria-invalid'] ? 'error' : undefined}
                  min={0}
                  max={GIAN_CACH_TOI_DA}
                  precision={0}
                  suffix={t('rpaLoi.form.giay')}
                  value={raO(than.retryDelaySeconds)}
                  onChange={(v) => dat('retryDelaySeconds', tuO(v))}
                />
              )}
            </Field>
            <Field label={t('rpaLoi.form.khiHet')} hint={t('rpaLoi.form.khiHetGoiY')}>
              {(p) => (
                <Select<KhiHetLan>
                  {...p}
                  className="w-full"
                  value={than.onExhausted}
                  onChange={(v) => dat('onExhausted', v)}
                  options={KHI_HET.map((v) => ({ value: v, label: t(`rpaLoi.form.${v}`) }))}
                />
              )}
            </Field>
          </div>
        </section>

        <DongBatTat nhan={t('rpaLoi.form.bat')} goiY={t('rpaLoi.form.batGoiY')} bat={than.enabled} onDoi={(v) => dat('enabled', v)} />
      </form>
    </Dialog>
  );
}
